// admin-panel/src/pages/Home.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Monitor, Clock, BarChart3, Shield, Users, TrendingUp, CheckCircle, ArrowRight, Zap, Globe, Lock } from 'lucide-react';
import './Home.css'; // Import the CSS file

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Time Tracking",
      description: "Accurate tracking of employee work hours with detailed reports and automated time logging"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Productivity Analytics",
      description: "Real-time insights into team performance with intelligent productivity scoring"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Security & Privacy",
      description: "Enterprise-grade security with GDPR compliance and privacy controls"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Team Management",
      description: "Easy team organization, role-based access control, and department management"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Performance Reports",
      description: "Comprehensive weekly/monthly reports for data-driven decision making"
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Task Management",
      description: "Assign, track, and monitor tasks with automated progress tracking"
    }
  ];

  const stats = [
    { value: "500+", label: "Companies" },
    { value: "50K+", label: "Users" },
    { value: "98%", label: "Satisfaction" },
    { value: "24/7", label: "Support" }
  ];

  return (
    <div className="home-page">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white sticky-top border-bottom shadow-sm">
        <div className="container">
          <div className="d-flex align-items-center">
            <div className="logo-container me-3">
              <Monitor className="logo-icon" />
            </div>
            <div>
              <span className="navbar-brand fw-bold fs-3 mb-0">QTraq</span>
              <p className="mb-0 text-muted small">Employee Monitoring Suite</p>
            </div>
          </div>
          
          <div className="d-flex align-items-center">
            <button 
              onClick={() => navigate('/login')}
              className="btn btn-link text-dark me-3 text-decoration-none"
            >
              Sign In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="btn btn-primary-q"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section py-5">
        <div className="container">
          <div className="text-center">
            <div className="badge-q mb-4">
              <Zap className="me-2" />
              Trusted by 500+ companies worldwide
            </div>
            
            <h1 className="display-3 fw-bold mb-4">
              Modern Employee
              <span className="gradient-text d-block">Monitoring Made Simple</span>
            </h1>
            
            <p className="lead text-muted mb-5 mx-auto">
              QTraq helps you gain valuable insights into team productivity while maintaining trust and transparency. 
              Perfect for remote teams and hybrid workplaces.
            </p>
            
            <div className="d-flex flex-column flex-md-row gap-3 justify-content-center mb-5">
              <button
                onClick={() => navigate('/register')}
                className="btn btn-primary-q btn-lg d-flex align-items-center justify-content-center"
              >
                Start 14-Day Free Trial
                <ArrowRight className="ms-2" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="btn btn-outline-q btn-lg"
              >
                Schedule a Demo
              </button>
            </div>

            {/* Stats */}
            <div className="row g-4 justify-content-center">
              {stats.map((stat, index) => (
                <div key={index} className="col-6 col-md-3">
                  <div className="stat-card text-center p-4">
                    <div className="stat-value display-6 fw-bold mb-2">{stat.value}</div>
                    <div className="stat-label text-muted">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <div className="section-badge mb-3">
              <span className="text-uppercase fw-semibold">Features</span>
            </div>
            <h2 className="display-5 fw-bold mb-3">
              Everything You Need in One Platform
            </h2>
            <p className="lead text-muted mx-auto">
              Powerful tools designed to help you monitor, analyze, and improve team productivity
            </p>
          </div>

          <div className="row g-4">
            {features.map((feature, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <div className="feature-card h-100">
                  <div className="feature-icon mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="h4 fw-bold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted mb-0">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 position-relative">
        <div className="container">
          <div className="text-center">
            <h2 className="display-5 fw-bold text-white mb-4">
              Ready to Transform Your Team Management?
            </h2>
            <p className="lead text-white mb-5 mx-auto opacity-90">
              Join thousands of companies who trust QTraq for their employee monitoring needs. 
              Start your free trial today, no credit card required.
            </p>
            <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
              <button
                onClick={() => navigate('/register')}
                className="btn btn-light btn-lg text-primary fw-semibold"
              >
                Start Free Trial
              </button>
              <button
                onClick={() => navigate('/login')}
                className="btn btn-outline-light btn-lg"
              >
                Book a Demo
              </button>
            </div>
            <p className="text-white mt-4 small opacity-75">
              No credit card required • 14-day free trial • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-5 bg-dark">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0">
              <div className="d-flex align-items-center">
                <div className="footer-logo me-3">
                  <Monitor className="text-white" />
                </div>
                <div>
                  <span className="text-white fw-bold fs-4">QTraq</span>
                  <p className="text-muted mb-0 small">Employee Monitoring Suite</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-6">
              <div className="d-flex flex-wrap justify-content-md-end gap-4">
                <Link to="/" className="text-muted text-decoration-none hover-white">
                  Home
                </Link>
                <a href="#" className="text-muted text-decoration-none hover-white">
                  Features
                </a>
                <a href="#" className="text-muted text-decoration-none hover-white">
                  Pricing
                </a>
                <a href="#" className="text-muted text-decoration-none hover-white">
                  Contact
                </a>
                <a href="#" className="text-muted text-decoration-none hover-white">
                  Privacy
                </a>
                <a href="#" className="text-muted text-decoration-none hover-white">
                  Terms
                </a>
                <button 
                  onClick={() => navigate('/login')}
                  className="btn btn-link text-primary text-decoration-none p-0"
                >
                  Login
                </button>
              </div>
            </div>
          </div>
          
          <div className="border-top border-dark mt-5 pt-4">
            <div className="row align-items-center">
              <div className="col-md-6 mb-3 mb-md-0">
                <p className="text-muted mb-0">© {new Date().getFullYear()} QTraq. All rights reserved.</p>
              </div>
              <div className="col-md-6">
                <div className="d-flex justify-content-md-end gap-4">
                  <span className="text-muted d-flex align-items-center">
                    <Globe className="me-2" />
                    Global
                  </span>
                  <span className="text-muted d-flex align-items-center">
                    <Lock className="me-2" />
                    GDPR Compliant
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;